// hooks/academicPlan/useBookmarkOperations.ts
/**
 * Bookmark operations hook
 * Manages adding, removing, and toggling bookmarked courses
 */
import { getErrorMessage } from '../../utils/errorUtils';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../useToast';
import { typedApi } from '../../services/typedApiClient';
import { cleanCourseForUI, type CourseForUI } from './helpers';
import type { AcademicPlan, EnrolledCourse } from '../../types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Course input for bookmark operations
 */
export interface BookmarkCourseInput {
  code: string;
  title?: string;
  course_title?: string;
  credits?: number;
}

/**
 * Parameters for the useBookmarkOperations hook
 */
export interface UseBookmarkOperationsParams {
  academicPlan: AcademicPlan | null;
  setAcademicPlan: React.Dispatch<React.SetStateAction<AcademicPlan>>;
  currentPlanId: number | null;
}

/**
 * Return type for the useBookmarkOperations hook
 */
export interface BookmarkOperations {
  addToBookmarks: (course: BookmarkCourseInput) => Promise<void>;
  removeFromBookmarks: (courseIndex: number) => Promise<void>;
  removeBookmarkByCode: (courseCode: string) => Promise<void>;
  toggleBookmark: (course: BookmarkCourseInput) => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for bookmark operations within an academic plan
 */
export const useBookmarkOperations = ({
  academicPlan,
  setAcademicPlan,
  currentPlanId
}: UseBookmarkOperationsParams): BookmarkOperations => {
  const { user } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();

  // Add course to bookmarks
  const addToBookmarks = useCallback(async (course: BookmarkCourseInput): Promise<void> => {
    if (!academicPlan || !currentPlanId) return;

    // Check if already bookmarked
    const exists = academicPlan.bookmarkedCourses.some(
      existingCourse => existingCourse.code === course.code
    );

    if (exists) {
      showWarning(`${course.code} is already bookmarked`);
      return;
    }

    // Clean course data before adding to bookmarks
    const cleanCourse = cleanCourseForUI(course as CourseForUI);
    if (!cleanCourse) return;

    // Add status field for EnrolledCourse compatibility
    const courseWithStatus: EnrolledCourse = {
      ...cleanCourse,
      status: 'planned'
    };

    // Optimistically update UI
    setAcademicPlan(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        bookmarkedCourses: [...prev.bookmarkedCourses, courseWithStatus]
      };
    });

    try {
      // Use dedicated bookmark endpoint
      await typedApi.academicPlans.addBookmark(currentPlanId, course.code);

      showSuccess(`${course.code} bookmarked successfully`);
    } catch (err) {
      // Revert on error
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          bookmarkedCourses: prev.bookmarkedCourses.filter(c => c.code !== course.code)
        };
      });
      showError(`Failed to bookmark ${course.code}: ${getErrorMessage(err)}`);
    }
  }, [academicPlan, setAcademicPlan, currentPlanId, showError, showSuccess, showWarning]);

  // Remove course from bookmarks by index
  const removeFromBookmarks = useCallback(async (courseIndex: number): Promise<void> => {
    if (!academicPlan || !currentPlanId || !user?.id) return;

    const courseToRemove = academicPlan.bookmarkedCourses[courseIndex];
    if (!courseToRemove) return;

    // Optimistically update UI
    setAcademicPlan(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        bookmarkedCourses: prev.bookmarkedCourses.filter((_, index) => index !== courseIndex)
      };
    });

    try {
      await typedApi.academicPlans.removeBookmark(currentPlanId, courseToRemove.code);

      showSuccess(`${courseToRemove.code} removed from bookmarks`);
    } catch (err) {
      // Revert on error
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          bookmarkedCourses: [
            ...prev.bookmarkedCourses.slice(0, courseIndex),
            courseToRemove,
            ...prev.bookmarkedCourses.slice(courseIndex)
          ]
        };
      });
      showError(`Failed to remove bookmark: ${getErrorMessage(err)}`);
    }
  }, [academicPlan, setAcademicPlan, currentPlanId, user?.id, showError, showSuccess]);

  // Helper function to remove bookmark by course code (for chat commands)
  const removeBookmarkByCode = useCallback(async (courseCode: string): Promise<void> => {
    if (!academicPlan) {
      throw new Error('Academic plan not loaded');
    }

    const courseIndex = academicPlan.bookmarkedCourses.findIndex(
      course => course.code === courseCode
    );

    if (courseIndex === -1) {
      throw new Error(`${courseCode} is not bookmarked`);
    }

    await removeFromBookmarks(courseIndex);
  }, [academicPlan, removeFromBookmarks]);

  // Toggle bookmark status of a course
  const toggleBookmark = useCallback((course: BookmarkCourseInput): void => {
    if (!academicPlan) return;

    const isBookmarked = academicPlan.bookmarkedCourses.some(
      existingCourse => existingCourse.code === course.code
    );

    if (isBookmarked) {
      const index = academicPlan.bookmarkedCourses.findIndex(
        existingCourse => existingCourse.code === course.code
      );
      if (index !== -1) {
        removeFromBookmarks(index);
      }
    } else {
      addToBookmarks(course);
    }
  }, [academicPlan, addToBookmarks, removeFromBookmarks]);

  // Memoize operations object
  return useMemo(() => ({
    addToBookmarks,
    removeFromBookmarks,
    removeBookmarkByCode,
    toggleBookmark
  }), [addToBookmarks, removeFromBookmarks, removeBookmarkByCode, toggleBookmark]);
};
